/// <reference types="@angular/localize" />

import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { CONFIG, IConfig } from './app/config';
import {
  httpInterceptorProviders,
  IS_MULTI_TENANT,
  sideMenuSecurityProvider,
  APP_VERSION,
  ALLOW_SELF_REGISTRATION
} from '@pcplus/security';
import {
  APP_SHORT_NAME,
  APP_MAIN_PAGE,
  APP_MAIN_ICON,
  APP_MAIN_ICON_FONT_SET
} from '@pcplus/core';
import {
  API_SERVER,
  AUTH_REDIRECT_PAGE,
  AVAILABLE_CURRENCIES,
  AVAILABLE_LOCALES,
  ACTIVITIES_CONFIG,
  ActivitiesConfig,
  AdditionalField
} from '@pcplus/domain-core';
import {
  STATIC_SIDE_MENU_ACTIONS,
  AVAILABLE_THEMES,
  DISABLE_NOTIFICATIONS,
  APP_FOOTER_TEXT,
  APP_FOOTER_LINK,
  GeneralMatPaginatorIntl
} from '@pcplus/infrastructure';
import {
  MatPaginatorIntl,
  MatPaginatorModule
} from '@angular/material/paginator';
import {
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { NgxQRCodeModule } from '@jonyadamit/ngx-qrcode-ivy';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { bootstrapApplication } from '@angular/platform-browser';
import { importProvidersFrom } from '@angular/core';
import { provideNativeDateAdapter, NativeDateAdapter, DateAdapter  } from '@angular/material/core';

function configListener(response: ProgressEvent) {
  try {
    const xhr = response.target as XMLHttpRequest;
    if (!xhr?.responseText) {
      throw new Error('Missing Configuration!');
    }
    const configuration = JSON.parse(xhr.responseText);

    // pass config to bootstrap process using an injection token
    bootstrapApplication(AppComponent, {
      providers: [
        importProvidersFrom(
          FormsModule,
          MatTableModule,
          MatSortModule,
          FlexLayoutModule,
          MatBadgeModule,
          MatCardModule,
          MatDividerModule,
          MatPaginatorModule,
          MatIconModule,
          MatButtonModule,
          MatFormFieldModule,
          MatInputModule,
          MatSelectModule,
          MatDialogModule,
          MatMenuModule,
          MatTooltipModule,
          MatCheckboxModule,
          MatChipsModule,
          NgxQRCodeModule,
          ClipboardModule,
          AppRoutingModule
        ),
        { provide: DateAdapter, useClass: NativeDateAdapter },
        provideNativeDateAdapter(),
        { provide: CONFIG, useValue: configuration },
        httpInterceptorProviders,
        {
          provide: APP_SHORT_NAME,
          useFactory: (c: IConfig) => c.shortApplicationName,
          deps: [CONFIG]
        },
        {
          provide: API_SERVER,
          useFactory: (c: IConfig) => c.apiServer,
          deps: [CONFIG]
        },
        {
          provide: AUTH_REDIRECT_PAGE,
          useFactory: (c: IConfig) => c.authRedirectPage,
          deps: [CONFIG]
        },
        {
          provide: IS_MULTI_TENANT,
          useFactory: (c: IConfig) => c.multiTenant,
          deps: [CONFIG]
        },
        {
          provide: APP_MAIN_PAGE,
          useFactory: (c: IConfig) => c.mainPage,
          deps: [CONFIG]
        },
        {
          provide: APP_MAIN_ICON,
          useFactory: (c: IConfig) => c.mainIcon,
          deps: [CONFIG]
        },
        {
          provide: APP_MAIN_ICON_FONT_SET,
          useFactory: (c: IConfig) => c.mainIconFontSet,
          deps: [CONFIG]
        },
        {
          provide: STATIC_SIDE_MENU_ACTIONS,
          useFactory: (c: IConfig) => c.sideMenuActions,
          deps: [CONFIG]
        },
        sideMenuSecurityProvider,
        {
          provide: AVAILABLE_THEMES,
          useFactory: (c: IConfig) => c.availableThemes,
          deps: [CONFIG]
        },
        {
          provide: AVAILABLE_CURRENCIES,
          useFactory: (c: IConfig) => c.availableCurrencies,
          deps: [CONFIG]
        },
        {
          provide: AVAILABLE_LOCALES,
          useFactory: (c: IConfig) => c.availableLocales,
          deps: [CONFIG]
        },
        {
          provide: DISABLE_NOTIFICATIONS,
          useFactory: (c: IConfig) => c.disableNotifications,
          deps: [CONFIG]
        },
        {
          provide: APP_FOOTER_TEXT,
          useFactory: (c: IConfig) => c.appFooterText,
          deps: [CONFIG]
        },
        {
          provide: APP_FOOTER_LINK,
          useFactory: (c: IConfig) => c.appFooterLink,
          deps: [CONFIG]
        },
        {
          provide: MatPaginatorIntl,
          useClass: GeneralMatPaginatorIntl
        },
        {
          provide: APP_VERSION,
          useFactory: (c: IConfig) => c.version,
          deps: [CONFIG]
        },
        {
          provide: ALLOW_SELF_REGISTRATION,
          useFactory: (c: IConfig) => c.allowSelfRegistration,
          deps: [CONFIG]
        },
        {
          provide: ACTIVITIES_CONFIG,
          useValue: new ActivitiesConfig([
            new AdditionalField(
              $localize`Resource Name`,
              'protectedResourceName'
            ),
            new AdditionalField(
              $localize`Resource User`,
              'protectedResourceUser'
            )
          ])
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations()
      ]
    }).catch((err) => console.error(err));
  } catch (error) {
    console.error(error);
  }
}

function configFailed(_evt: any) {
  console.error('Error: retrieving config.json');
}

const request = new XMLHttpRequest();
request.addEventListener('load', configListener);
request.addEventListener('error', configFailed);
request.open('GET', './assets/config.json');
request.send();
